import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import pnl3BalanceCache from './Pnl3BalanceCache';
import pnl3BalanceFetcher from './Pnl3BalanceFetcher';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';

const _name = 'Pnl3BalanceEngine';

/**
 * Cache-logic wrapper around Pnl3BalanceCache — singleton, same shape as
 * UserAssetEngine/ScenarioAssetEngine. Pnl3BalanceCache holds every summary the current
 * user has fetched (across all open windows); each Pnl3Widget reads its own sid out of
 * it and filters the notify event by sid so it only reacts to its own asset.
 *
 * FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672): extends
 * WssEventHandlerBase for the BE balance-push event, on top of the plain REST fetch.
 */
class Pnl3BalanceEngine extends WssEventHandlerBase {
    _name = _name;

    bootup() {
        super.bootup();
        this.clear();

        this._unsub = this._wsNotifier.subscribe(events.OnPnl3BalanceUpdate_v1, this.onMessage);
    }

    clear() {
        super.clear();

        if (this._unsub) this._unsub();
    }

    /**
     * BE stuurt per sid de volledige, actuele summary (geen delta). Geen JSON.parse
     * hier, WssEventNotifier levert al gedeserialiseerd.
     * @param {{sid: number|string, summary: object}} data
     */
    onMessage = (data) => {
        try {
            pnl3BalanceCache.addOrUpdate([
                {
                    ...data,
                    rk: String(data.sid),
                },
            ]);

            this._notify(data.sid);
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * @param {number|string} sid
     */
    getSummary = async (sid) => {
        try {
            const r = pnl3BalanceCache.get(String(sid));
            if (r) return r;

            return await this.fetch(sid);
        } catch (error) {
            this.logError('getSummary', `ex=${error.message}`);
            return null;
        }
    };

    /**
     * @param {number|string} [sid] - 0 (default) = alle assets in 1 call (PageContent-boot use-case), primet de hele cache tegelijk
     */
    fetch = async (sid = 0) => {
        try {
            const result = await pnl3BalanceFetcher.fetch(sid);

            if (Number(sid) === 0) {
                const items = result ?? [];
                items.forEach((s) => pnl3BalanceCache.addOrUpdate([{ ...s, rk: String(s.sid) }]));
                items.forEach((s) => this._notify(s.sid));
                return items;
            }

            if (result) pnl3BalanceCache.addOrUpdate([{ ...result, rk: String(sid) }]);
            this._notify(sid);
            return result;
        } catch (error) {
            this.logError('fetch', `ex=${error.message}`);
            return Number(sid) === 0 ? [] : null;
        }
    };

    // notify draagt alleen de sid — component leest de summary altijd terug uit
    // pnl3BalanceCache.get(String(sid)), nooit uit het notify-payload zelf (V2-regel).
    _notify = (sid) => {
        ComponentNotifier.notify(_name, 'dataChanged', { sid: String(sid) });
    };
}

const pnl3BalanceEngine = new Pnl3BalanceEngine();
export default pnl3BalanceEngine;
